import React from "react";
import { useMediaQuery } from "@mui/system";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { DateField, RecordContextProvider, useRecordContext } from "react-admin";
import { settings } from "app";
import { useTravelerNames } from "components/Leaves/utils/useTravelerNames";
import { resolveTravelerKind } from "components/Leaves/Requests/utils/travelers";
import { TravelerKindChoices } from "utils/choices";
import { TravelerKinds } from "utils/constants";
import { SectionTitle } from "features/SectionTitle";

const EMPTY = "—";

const yesNo = value => (value ? "Да" : "Не");

const kindLabel = kind => TravelerKindChoices.find(choice => choice.id === kind)?.name ?? EMPTY;

const CONTRACT_COLUMNS = [
  { label: "№ на договор", render: t => t?.contractNumber || EMPTY },
  {
    label: "Дата на договор",
    render: () => (
      <DateField
        source="contractDate"
        locales="bg-BG"
        emptyText={EMPTY}
      />
    ),
  },
  { label: "Друг работодател", render: t => yesNo(t?.hasOtherEmployer) },
  { label: "Съгласие", render: t => yesNo(t?.employerConsentProvided) },
];

const FACULTY_COLUMN = { label: "Факултет", render: t => t?.faculty || EMPTY };
const FACULTY_NUMBER_COLUMN = { label: "Факултетен №", render: t => t?.facultyNumber || EMPTY };

// Only the groups some traveler actually fills — a domestic staff trip stays a two-column table.
// A doctoral candidate carries no faculty number, so that column needs an actual student.
const resolveColumns = kinds => [
  { label: "Тип", render: (t, kind) => kindLabel(kind) },
  { label: "Име", render: (t, kind, name) => name || EMPTY },
  ...(kinds.includes(TravelerKinds.External) ? CONTRACT_COLUMNS : []),
  ...(kinds.some(kind => kind === TravelerKinds.Student || kind === TravelerKinds.Doctoral)
    ? [FACULTY_COLUMN]
    : []),
  ...(kinds.includes(TravelerKinds.Student) ? [FACULTY_NUMBER_COLUMN] : []),
];

const TravelersTable = () => {
  const record = useRecordContext();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const travelers = record?.travelers ?? [];
  const names = useTravelerNames(travelers);

  if (!travelers.length) return null;

  const kinds = travelers.map(resolveTravelerKind);
  const columns = resolveColumns(kinds);

  return (
    <>
      <SectionTitle
        title="Пътуващи"
        textAlign="center"
        sx={{ marginTop: "2rem" }}
      />
      <Box sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell
                    key={column.label}
                    sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {travelers.map((traveler, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:nth-of-type(even)": {
                      backgroundColor: theme => theme.palette.tableStripe?.main ?? theme.palette.action.hover,
                    },
                  }}
                >
                  <RecordContextProvider value={traveler}>
                    {columns.map(column => (
                      <TableCell
                        key={column.label}
                        sx={{ verticalAlign: "top" }}
                      >
                        {column.render(traveler, kinds[index], names[index])}
                      </TableCell>
                    ))}
                  </RecordContextProvider>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default TravelersTable;
